import { Injectable } from '@angular/core';

import { OpenPayForm } from '@getopenpay/openpay-js';

import { IOpenPayConfig } from 'app/interfaces/payments.interfaces';

@Injectable({
  providedIn: 'root'
})
export class OpenPayService {
  private static currentInstance: OpenPayForm | null = null;
  private static isDestroying: boolean = false;
  private static destroyPromise: Promise<void> | null = null;

  async createInstance(config: IOpenPayConfig): Promise<OpenPayForm> {
    await this.waitForDestruction();

    if (OpenPayService.currentInstance) {
      await this.destroyInstanceInternal();
    }

    try {
      OpenPayService.currentInstance = new OpenPayForm(config);
      return OpenPayService.currentInstance;
    } catch (error) {
      console.error('Error creating OpenPay instance:', error);
      throw error;
    }
  }

  destroyInstance(): void {
    if (!OpenPayService.currentInstance || OpenPayService.isDestroying) return;

    OpenPayService.destroyPromise = this.destroyInstanceInternal();
  }

  private async destroyInstanceInternal(): Promise<void> {
    if (!OpenPayService.currentInstance || OpenPayService.isDestroying) return;

    OpenPayService.isDestroying = true;

    try {
      await OpenPayService.currentInstance.destroy();
      OpenPayService.currentInstance = null;
    } catch (error) {
      console.error('Error destroying OpenPay instance:', error);
      OpenPayService.currentInstance = null;
    } finally {
      OpenPayService.isDestroying = false;
      OpenPayService.destroyPromise = null;
    }
  }

  private async waitForDestruction(): Promise<void> {
    if (OpenPayService.destroyPromise !== null) {
      try {
        await Promise.race([
          OpenPayService.destroyPromise,
          new Promise(resolve => setTimeout(resolve, 3000))
        ]);
      } catch (error) {
        console.warn('Error waiting for destruction:', error);
      }
    }

    const maxWaitTime = 3000;
    const checkInterval = 50;
    let elapsed = 0;

    while (OpenPayService.isDestroying && elapsed < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      elapsed += checkInterval;
    }

    if (elapsed >= maxWaitTime) {
      OpenPayService.isDestroying = false;
      OpenPayService.currentInstance = null;
      OpenPayService.destroyPromise = null;
    }
  }

  hasInstance(): boolean {
    return OpenPayService.currentInstance !== null;
  }
}
